import { useMemo } from "react";
import type { ImmutableObject } from "@/lib/immutable";
import { createStore, type Store, useStore } from "./store";

export type ProductBrowserViewType = "grid" | "list";

export class ProductBrowserModel {
	view: ProductBrowserViewType = "grid";
	itemWidth: number = 200;
	expandedView: boolean = true;
}

const storefront_productbrowser_key = "storefront-productbrowser";
let initialState = new ProductBrowserModel();
const localstorageJson = window.localStorage.getItem(
	storefront_productbrowser_key,
);
if (localstorageJson) {
	try {
		initialState = JSON.parse(localstorageJson);

		// the expanded view button was hidden, we are going to force it to true in case anyone has it saved as false
		// not ready to delete this yet until I'm sure people don't want this feature back
		initialState.expandedView = true;
	} catch (ex) {
		console.error(ex);
	}
}
const productBrowserGlobalStore =
	createStore<ProductBrowserModel>(initialState);

export class ProductBrowserState {
	private productBrowserState: Store<ProductBrowserModel>;

	constructor(productBrowserState: Store<ProductBrowserModel>) {
		this.productBrowserState = productBrowserState;
	}

	get(): ImmutableObject<ProductBrowserModel> {
		return this.productBrowserState.getState();
	}

	private saveToLocalStorage() {
		const obj = this.productBrowserState.getState();
		let json = "";
		if (obj) {
			json = JSON.stringify(obj);
		}
		window.localStorage.setItem(storefront_productbrowser_key, json);
	}

	setView(value: ProductBrowserViewType) {
		this.productBrowserState.setState((prev) => ({ ...prev, view: value }));
		this.saveToLocalStorage();
	}

	setExpandedView(value: boolean) {
		this.productBrowserState.setState((prev) => ({
			...prev,
			expandedView: value,
		}));
		this.saveToLocalStorage();
	}

	setItemWidth(value: number) {
		this.productBrowserState.setState((prev) => ({
			...prev,
			itemWidth: value,
		}));
		this.saveToLocalStorage();
	}
}

export function useProductBrowserState(): ProductBrowserState {
	useStore(productBrowserGlobalStore);
	return useMemo(() => new ProductBrowserState(productBrowserGlobalStore), []);
}
